import { AvatarView } from "@/components/AvatarView";
import { PointsBubble } from "@/components/PointsBubble";
import { useI18n } from "@/lib/i18n";
import { Zap, TrendingUp, TrendingDown, Gift, Trophy, Award, ExternalLink } from "lucide-react";

export function ReadonlyProfile({ student }) {
  const { t } = useI18n();
  const att = student.attendance_summary || {};
  const fmt = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="space-y-6">
      <div className="bg-card rounded-3xl border border-border overflow-hidden">
        <div className="h-20" style={{ background: `linear-gradient(135deg, ${student.customization?.banner_color || "#00E5FF"}, ${(student.customization?.banner_color || "#00E5FF")}88)` }} />
        <div className="px-6 pb-6 -mt-10 flex items-end gap-4">
          <div className="relative">
            <AvatarView student={student} size={96} />
            <span className="absolute bottom-0 right-0 translate-x-1 translate-y-1 z-40"><PointsBubble value={student.points} size="lg" testid="portal-points" /></span>
          </div>
          <div className="flex-1 pb-2">
            <h1 className="font-display text-2xl font-extrabold">{student.name}</h1>
            {student.drive_url && <a data-testid="portal-drive-btn" href={student.drive_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"><ExternalLink className="w-4 h-4" /> {t("driveFolder")}</a>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[[Zap, t("currentPoints"), student.points, "#00E5FF"], [TrendingUp, t("earned"), student.points_earned, "#00C853"], [TrendingDown, t("spent"), student.points_spent, "#FF3333"], [Gift, t("rewardsTitle"), (student.rewards_redeemed || []).length, "#AA00FF"]].map(([I, l, v, col], i) => (
          <div key={i} className="bg-card rounded-2xl border border-border p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: col + "22", color: col }}><I className="w-5 h-5" /></div><div><div className="text-xl font-display font-extrabold leading-none">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div></div>
        ))}
      </div>

      <div className="bg-card rounded-2xl border border-border p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-yellow-500" /> {t("medals")}</h3>
        {(student.medals || []).length === 0 ? <p className="text-sm text-muted-foreground">{t("noMedalsYet")}</p> : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
            {student.medals.map((m, i) => (
              <a key={i} href={m.url || undefined} target="_blank" rel="noreferrer" className="flex flex-col items-center text-center group">
                {m.image ? <img src={m.image} alt={m.name} className="w-16 h-16 object-contain group-hover:scale-110 transition-transform" /> : <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-2xl">🏆</div>}
                <span className="text-xs font-semibold mt-1 flex items-center gap-0.5">{m.name} {m.url && <ExternalLink className="w-3 h-3" />}</span>
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-4"><Award className="w-5 h-5 text-fuchsia-500" /> {t("badges")}</h3>
        {(student.badges || []).length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
          <div className="flex flex-wrap gap-2">{student.badges.map((b, i) => <span key={i} className="px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5" style={{ background: (b.color || "#888") + "18", color: b.color || "#888" }}>{b.emoji} {b.name}</span>)}</div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-border p-5">
          <h3 className="font-display font-bold mb-3">{t("attendance")}</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-600 font-bold text-center">{att.present || 0}<div className="text-xs font-normal">{t("present")}</div></div>
            <div className="p-3 rounded-xl bg-red-500/10 text-red-500 font-bold text-center">{att.absent || 0}<div className="text-xs font-normal">{t("absent")}</div></div>
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-border p-5">
          <h3 className="font-display font-bold mb-3">{t("history")}</h3>
          <div className="space-y-1.5 max-h-52 overflow-auto scrollbar-thin">
            {(student.history_items || []).slice(0, 20).map((h) => (
              <div key={h.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted"><div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">{h.type === "medal" ? "🏆" : h.type === "badge" ? "🏅" : h.type === "reward" ? "🎁" : h.emoji}</div><div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{h.action}</div><div className="text-xs text-muted-foreground">{fmt(h.timestamp)}</div></div>{(h.type === "event" || h.type === "reward") && <span className={`font-bold ${h.points >= 0 ? "text-emerald-500" : "text-red-500"}`}>{h.points >= 0 ? "+" : ""}{h.points}</span>}</div>
            ))}
            {(student.history_items || []).length === 0 && <p className="text-sm text-muted-foreground">Sin movimientos.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
